import { ScrapingField, ScrapeProgress } from "@/types/scraper";
// ScraperAPI import removed
import { OxyLabsService } from "./oxyLabsApi";
import { createConfig, createLog, createResult } from "./db";
import { ValidationError, TimeoutError, NetworkError } from "./errors";
import { ErrorLogger } from "./errorLogging";
import { validateUrl } from "./testUtils";
import { ElementDetector } from "./elementDetection";

export interface ScraperConfig {
  enablePagination?: boolean;
  paginationSelector?: string;
  maxPages?: number;
  verbose?: boolean;
  onPaginationSelect?: () => Promise<string>;
  productLinkSelector?: string;
  onProductLinkSelect?: () => Promise<string>;
  useScraperApi?: boolean;
  scraperApiConfig?: {
    apiKey: string;
    country?: string;
    device?: "desktop" | "mobile" | "tablet";
    premium?: boolean;
    renderJs?: boolean;
    keepCookies?: boolean;
  };
  useOxylabs?: boolean;
  oxylabsConfig?: {
    username: string;
    password: string;
    country?: string;
    device?: "desktop" | "mobile" | "tablet";
    renderJs?: boolean;
  };
  useDeepSeekAI?: boolean;
  deepSeekConfig?: {
    enabled: boolean;
    confidenceThreshold: number;
    preferAiSuggestions: boolean;
    modelName: string;
    useLocalServer?: boolean;
    localServerUrl?: string;
    localServerPort?: number;
    localServerStatus?: "online" | "offline" | "unknown";
  };
}

export class ScraperService {
  private projectId: string;
  private configId: string;
  private url: string;
  private fields: ScrapingField[];
  private config: ScraperConfig;
  private onProgress?: (progress: ScrapeProgress) => void;
  private onElementSelect?: (fieldId: string) => Promise<string>;
  // ScraperAPI reference removed
  private oxylabs?: OxyLabsService;
  private isRunning: boolean = false;
  private isPaused: boolean = false;
  private shouldStop: boolean = false;
  private DEBUG = process.env.NODE_ENV !== "production";
  private useDeepSeekAI = false;
  private deepSeekConfidenceThreshold = 0.7;
  private preferAiSuggestions = false;
  private deepSeekModelName = "deepseek-coder-v1.5";
  private deepSeekServerUrl = "/api/deepseek/detect";

  constructor(
    projectId: string,
    url: string,
    fields: ScrapingField[],
    config: ScraperConfig = {},
    onProgress?: (progress: ScrapeProgress) => void,
    onElementSelect?: (fieldId: string) => Promise<string>,
  ) {
    this.projectId = projectId;
    this.url = url;
    this.fields = fields;
    this.config = config;
    this.onProgress = onProgress;
    this.onElementSelect = onElementSelect;
    this.configId = ""; // Will be set after initialization
  }

  private async log(
    message: string,
    status: "info" | "warning" | "error" | "success" = "info",
    currentProgress?: number,
    total?: number,
  ): Promise<void> {
    if (this.DEBUG) {
      console.log(`[${status.toUpperCase()}] ${message}`, {
        progress: currentProgress,
        total,
        timestamp: new Date().toISOString(),
      });
    }

    const progress: ScrapeProgress = {
      status,
      message,
      timestamp: new Date().toISOString(),
      progress: currentProgress,
      totalProgress: total,
    };

    this.onProgress?.(progress);

    // Log to database if configId is available
    if (this.configId) {
      await createLog({
        projectId: this.projectId,
        configId: this.configId,
        status,
        message,
        progress: currentProgress,
        totalProgress: total,
      });
    }
  }

  public async initialize(): Promise<void> {
    // Create a new config in the database
    const scrapeConfig = await createConfig({
      projectId: this.projectId,
      url: this.url,
      enablePagination: this.config.enablePagination,
      paginationSelector: this.config.paginationSelector,
      maxPages: this.config.maxPages,
      verboseMode: this.config.verbose,
    });

    this.configId = scrapeConfig.id;

    // ScraperAPI initialization removed

    // Initialize Oxylabs if enabled
    if (
      this.config.useOxylabs &&
      this.config.oxylabsConfig?.username &&
      this.config.oxylabsConfig?.password
    ) {
      await this.log("Initializing Oxylabs...");
      try {
        this.oxylabs = new OxyLabsService(this.config.oxylabsConfig);
        await this.log("Oxylabs initialized successfully");
      } catch (error) {
        await this.log(
          `Failed to initialize Oxylabs: ${error instanceof Error ? error.message : String(error)}`,
          "error",
        );
      }
    }

    // Initialize DeepSeek AI if enabled
    if (this.config.useDeepSeekAI && this.config.deepSeekConfig) {
      await this.log("Initializing DeepSeek AI...");
      try {
        this.useDeepSeekAI = this.config.deepSeekConfig.enabled;
        this.deepSeekConfidenceThreshold =
          this.config.deepSeekConfig.confidenceThreshold;
        this.preferAiSuggestions =
          this.config.deepSeekConfig.preferAiSuggestions;
        this.deepSeekModelName = this.config.deepSeekConfig.modelName;

        // Set server URL if using local server
        if (this.config.deepSeekConfig.useLocalServer) {
          const baseUrl =
            this.config.deepSeekConfig.localServerUrl || "http://localhost";
          const port = this.config.deepSeekConfig.localServerPort || 8000;
          this.deepSeekServerUrl = `${baseUrl}:${port}/api/detect`;
          await this.log(
            `Using local DeepSeek server at ${this.deepSeekServerUrl}`,
          );
        } else {
          this.deepSeekServerUrl = "/api/deepseek/detect";
        }

        // Configure ElementDetector with DeepSeek AI settings
        ElementDetector.configureAI({
          provider: "local",
          enabled: this.useDeepSeekAI,
          confidenceThreshold: this.deepSeekConfidenceThreshold,
          modelName: this.deepSeekModelName,
          localServerUrl: this.deepSeekServerUrl,
          preferAiSuggestions: this.preferAiSuggestions,
          useLocalServer: this.config.deepSeekConfig?.useLocalServer || false,
          detectionMode: "balanced",
          includePageContext: true,
          extractValues: true,
          requestTimeout: 10000,
          maxRetries: 2,
        });

        await this.log("DeepSeek AI initialized successfully");
      } catch (error) {
        await this.log(
          `Failed to initialize DeepSeek AI: ${error instanceof Error ? error.message : String(error)}`,
          "error",
        );
      }
    }
  }

  public async start(): Promise<Record<string, string>> {
    if (!this.url) {
      throw new Error("URL is required");
    }

    if (!Array.isArray(this.fields) || this.fields.length === 0) {
      throw new Error("At least one field is required");
    }

    this.isRunning = true;
    this.shouldStop = false;
    const results: Record<string, string> = {};

    try {
      // Validate URL
      await this.log("Validating URL...");
      const urlValidation = await validateUrl(this.url, this.oxylabs);
      if (!urlValidation.valid) {
        throw new ValidationError(`Invalid URL: ${urlValidation.error}`);
      }

      // Add timeout for initial page load
      const pageLoadTimeout = new Promise((_, reject) => {
        setTimeout(
          () => reject(new TimeoutError("Page load timed out")),
          30000,
        );
      });

      try {
        await Promise.race([
          new Promise((resolve) => setTimeout(resolve, 1000)), // Simulated page load
          pageLoadTimeout,
        ]);
      } catch (error) {
        if (error instanceof TimeoutError) {
          throw error;
        }
        throw new NetworkError("Failed to load page");
      }

      await this.log("Initializing scraper...");
      await this.log(`Preparing to fetch ${this.url}...`);

      // Simulate page load
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await this.log("Page loaded successfully");

      let currentPage = 1;
      const activeFields = this.fields.filter((f) => f.active);
      const productsPerPage = 5; // Mock number of products per page
      const totalOperations =
        activeFields.length *
        productsPerPage *
        (this.config.enablePagination ? this.config.maxPages || 1 : 1);
      let completedOperations = 0;

      let paginationSelector = this.config.paginationSelector || "";
      let productLinkSelector = this.config.productLinkSelector || "";

      do {
        if (this.shouldStop) {
          await this.log("Scraping stopped by user", "warning");
          break;
        }

        while (this.isPaused) {
          await this.log("Scraping paused", "warning");
          await new Promise((resolve) => setTimeout(resolve, 1000));
          if (this.shouldStop) {
            await this.log("Scraping stopped while paused", "warning");
            break;
          }
        }

        if (currentPage > 1) {
          await this.log(`Processing page ${currentPage}...`);
        }

        // Get product link selector if needed
        if (
          (!productLinkSelector || this.config.verbose) &&
          this.config.onProductLinkSelect
        ) {
          await this.log("Please select a product link/card...", "warning");
          productLinkSelector = await this.config.onProductLinkSelect();
          await this.log(
            `Product link selector updated: ${productLinkSelector}`,
          );
        }

        // Simulate finding product links
        await this.log("Finding product links on page...");
        await new Promise((resolve) => setTimeout(resolve, 500));
        const mockProductUrls = Array(productsPerPage)
          .fill(0)
          .map((_, i) => `${this.url}/product-${currentPage}-${i + 1}`);
        await this.log(
          `Found ${mockProductUrls.length} products on page ${currentPage}`,
        );

        // Process each product
        for (const productUrl of mockProductUrls) {
          if (this.shouldStop) break;

          while (this.isPaused) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            if (this.shouldStop) break;
          }

          await this.log(`Navigating to product: ${productUrl}`);
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Process each active field for this product
          for (const field of activeFields) {
            if (this.shouldStop) break;

            while (this.isPaused) {
              await new Promise((resolve) => setTimeout(resolve, 1000));
              if (this.shouldStop) break;
            }

            this.onProgress?.({
              status: "info",
              message: `Processing field: ${field.label}`,
              timestamp: new Date().toISOString(),
              currentField: {
                id: field.id,
                label: field.label,
              },
              currentUrl: productUrl,
            });
            await new Promise((resolve) => setTimeout(resolve, 800));

            try {
              // Validate field
              if (!field.id || !field.label) {
                throw new Error(
                  `Invalid field configuration: ${JSON.stringify(field)}`,
                );
              }

              // Use DeepSeek AI for enhanced data extraction if enabled
              let extractedData = "";
              let confidenceScore = 0.95; // Default confidence score

              if (this.useDeepSeekAI) {
                try {
                  await this.log(
                    `Using DeepSeek AI to extract ${field.label}...`,
                  );
                  // In a real implementation, this would call the DeepSeek API
                  // For now, we'll simulate AI-enhanced extraction with a delay
                  await new Promise((resolve) => setTimeout(resolve, 300));

                  // Simulate AI extraction with more realistic data
                  const aiData = `AI-Enhanced ${field.label} from ${productUrl}`;
                  const aiConfidence = 0.75 + Math.random() * 0.2; // Between 0.75 and 0.95

                  extractedData = aiData;
                  confidenceScore = aiConfidence;

                  await this.log(
                    `DeepSeek AI extracted data with ${Math.round(aiConfidence * 100)}% confidence`,
                  );
                } catch (error) {
                  await this.log(
                    `DeepSeek AI extraction failed: ${error instanceof Error ? error.message : String(error)}`,
                    "error",
                  );
                  // Fall back to traditional extraction
                  extractedData = `Sample ${field.label} from ${productUrl}`;
                }
              } else {
                // Traditional data extraction (simulated)
                extractedData = `Sample ${field.label} from ${productUrl}`;
              }

              if (
                (this.config.verbose || !field.selector) &&
                this.onElementSelect
              ) {
                await this.log(
                  `Manual selection mode for ${field.label}...`,
                  "warning",
                );
                const selector = await this.onElementSelect(field.id);

                // In a real implementation, you would store this selector
                // and use it for future scraping operations
                if (selector) {
                  await this.log(
                    `Selector for ${field.label} set to: ${selector}`,
                    "success",
                  );
                  // field.selector = selector; // Would update the field's selector
                }
              }

              // Save result to database
              await createResult({
                projectId: this.projectId,
                configId: this.configId,
                productUrl,
                fieldId: field.id,
                value: extractedData,
                confidence: confidenceScore,
              });

              // Update local results
              if (!results[field.id]) {
                results[field.id] = extractedData;
              } else {
                results[field.id] += `\n${extractedData}`;
              }

              completedOperations++;
              await this.log(
                `Successfully extracted ${field.label}`,
                "success",
                completedOperations,
                totalOperations,
              );
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : "Unknown error";
              await this.log(
                `Failed to extract ${field.label}: ${errorMessage}`,
                "error",
              );
            }
          }

          await this.log("Returning to listing page...");
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        if (
          this.config.enablePagination &&
          currentPage < (this.config.maxPages || 1)
        ) {
          try {
            // If no pagination selector is set or in verbose mode, request manual selection
            if (
              (!paginationSelector || this.config.verbose) &&
              this.config.onPaginationSelect
            ) {
              await this.log(
                "Please select the 'Next Page' button...",
                "warning",
              );
              paginationSelector = await this.config.onPaginationSelect();
              await this.log(
                `Pagination selector updated: ${paginationSelector}`,
              );
            } else if (!paginationSelector) {
              await this.log(
                "Pagination selector is required when pagination is enabled",
                "error",
              );
              break;
            }

            await this.log(
              `Looking for next page using selector: ${paginationSelector}`,
            );
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Simulate pagination
            const hasNextPage = currentPage < (this.config.maxPages || 1);

            if (!hasNextPage) {
              await this.log("No more pages found", "info");
              break;
            }

            await this.log("Moving to next page...");
            await new Promise((resolve) => setTimeout(resolve, 500));
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";
            await this.log(
              `Failed to navigate to next page: ${errorMessage}`,
              "error",
            );
            break;
          }
        }

        currentPage++;
      } while (
        this.config.enablePagination &&
        currentPage <= (this.config.maxPages || 1)
      );

      const totalResults = Object.keys(results).length;
      await this.log(
        `Scraping completed successfully. Found ${totalResults} results.`,
        "success",
      );
      return results;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      await this.log(`Error: ${errorMessage}`, "error");

      // Log the error with metadata
      await ErrorLogger.getInstance().logError(
        error instanceof Error ? error : new Error(errorMessage),
        {
          url: this.url,
          projectId: this.projectId,
          configId: this.configId,
          fields: this.fields.map((f) => ({ id: f.id, label: f.label })),
          config: this.config,
        },
      );

      throw error;
    } finally {
      this.isRunning = false;
      this.isPaused = false;
    }
  }

  public pause(): void {
    if (this.isRunning && !this.isPaused) {
      this.isPaused = true;
    }
  }

  public resume(): void {
    if (this.isRunning && this.isPaused) {
      this.isPaused = false;
    }
  }

  public stop(): void {
    this.shouldStop = true;
    this.isPaused = false;
  }

  public isActive(): boolean {
    return this.isRunning;
  }

  public isPausedState(): boolean {
    return this.isPaused;
  }
}
